/**
 * ErrorBoundary — shared recovery shell for the venue booth and admin console.
 *
 * Design goals (Feb 2026):
 *   - When a page component throws, the OUTER app shell (Layout / AdminLayout
 *     sidebar + PlayerBar) stays mounted. This is critical: MusicContext lives
 *     above the boundary, so if the booth's currently-playing song crashes the
 *     UI, the audio KEEPS PLAYING while the host recovers.
 *   - Friendly, on-brand recovery card — zinc-900 body with fuchsia accent to
 *     match the redesigned booth. No stack traces, no red screen of death.
 *   - Two escape hatches:
 *       (a) "Reload page"  — soft reset: bumps a resetKey to remount children
 *       (b) "Refresh app"  — hard reload: window.location.reload()
 *   - Auto-recovers on route change: navigating away silently drops the
 *     error state so returning to a previously-crashed page can succeed
 *     (e.g., after a hot-reload during dev, or after a transient network
 *     race).
 *   - Reports the crash to /api/telemetry/frontend-error so a spike after
 *     a deploy shows up in ops without waiting for a customer complaint.
 *
 * React 18: componentDidCatch fires exactly once per uncaught render error;
 * we swallow it (log to console + POST to telemetry) and render the
 * recovery UI. We DO NOT rely on getDerivedStateFromError alone — we also
 * need componentDidCatch for the side effects (telemetry + console.error
 * so the deploy-blocker check still shows in prod logs).
 */
import React from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { RefreshCcw, RotateCcw, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Console.error keeps the standard React dev-tools + Sentry-like
    // integrations happy. Telemetry POST is fire-and-forget.
    console.error('[ErrorBoundary]', error, info);
    try {
      axios.post(`${API}/telemetry/frontend-error`, {
        message: String(error?.message || error || 'Unknown error').slice(0, 400),
        stack: String(error?.stack || '').slice(0, 4000),
        component_stack: String(info?.componentStack || '').slice(0, 2000),
        path: (typeof window !== 'undefined' && window.location?.pathname) || '',
        user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
        at: new Date().toISOString(),
      }).catch(() => {});
    } catch { /* telemetry must never block recovery */ }
  }

  // If the parent bumps resetKey (route change), drop the error so the new
  // page gets a clean render pass.
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  softReset = () => this.setState({ error: null });

  hardReload = () => {
    try {
      window.location.reload();
    } catch { /* noop */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    const scope = this.props.scope || 'this page';
    return (
      <div
        className="w-full min-h-[60vh] flex items-center justify-center p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="w-full max-w-md rounded-3xl bg-zinc-900 border border-zinc-800 shadow-[0_0_40px_rgba(217,70,239,0.15)] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-fuchsia-400" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">Recovery</p>
              <h2 className="text-lg font-black text-white tracking-tight" style={{ fontFamily: 'Outfit' }}>
                Something broke on {scope}
              </h2>
            </div>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed mb-5">
            Your music keeps playing — this recovery card only replaces the crashed page.
            Try reloading it below, and if that doesn't help, do a full app refresh.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={this.softReset}
              className="h-11 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-bold flex items-center justify-center gap-2 tracking-wider active:scale-95 transition-transform"
              data-testid="error-boundary-soft-reset"
            >
              <RotateCcw className="w-4 h-4" />
              Reload page
            </button>
            <button
              onClick={this.hardReload}
              className="h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white font-semibold flex items-center justify-center gap-2 border border-zinc-700 transition-colors"
              data-testid="error-boundary-hard-reload"
            >
              <RefreshCcw className="w-4 h-4" />
              Refresh app
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 mt-4 text-center font-mono uppercase tracking-widest">
            Auto-recovers when you switch pages
          </p>
        </div>
      </div>
    );
  }
}

// Wrap the class component so we can drive `resetKey` from a hook — this is
// how route changes silently reset the boundary. Router v6 exposes location
// via useLocation; we use pathname as the reset key. The boundary is only
// ever mounted inside <BrowserRouter> (via Layout / AdminLayout), so
// useLocation is always safe here.
export const ErrorBoundary = ({ children, scope }) => {
  const location = useLocation();
  return (
    <ErrorBoundaryInner resetKey={location.pathname} scope={scope}>
      {children}
    </ErrorBoundaryInner>
  );
};

export default ErrorBoundary;
